import { Box, TableCell, TableRow, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { formatDate } from '../../../helpers/formatDate';
import { numberToUSDCurrency } from '../../../helpers/numberToUSDCurrency';
import { FirmTransaction } from '../../../types';
import { palette } from '../../../palette';

interface FirmTransactionsTableDataRowProps {
  tableData: FirmTransaction[];
}

const useStyles = makeStyles()(() => ({
  creditLeftBorder: {
    position: 'relative',

    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '2px',
      bottom: '2px',
      width: '4px',
      backgroundColor: palette.success.main,
      borderRadius: '2px',
    },
  },
  debitLeftBorder: {
    position: 'relative',

    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '2px',
      bottom: '2px',
      width: '4px',
      backgroundColor: palette.error.main,
      borderRadius: '2px',
    },
  },
  createdCellContent: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
  queuedTag: {
    backgroundColor: '#FFF7CC',
    color: '#8A6D1F',
    border: '1px solid #F0E2A6',
    borderRadius: '999px',
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: 600,
    lineHeight: '120%',
    fontFamily: 'Inter',
    whiteSpace: 'nowrap',
  },
}));

const getTypeColor = (type: string) => {
  if (type === 'CREDIT') return palette.success.main;
  if (type === 'DEBIT') return palette.error.main;
  return undefined;
};

const FirmTransactionsTableDataRow = ({
  tableData,
}: FirmTransactionsTableDataRowProps) => {
  const { classes } = useStyles();

  const formattedList = tableData.map((transaction) => ({
    lastUpdatedOn: formatDate(transaction.lastUpdatedOn),
    account: transaction.accountName,
    transactionId: transaction.transactionId,
    caseReferenceId: transaction.caseReferenceId ?? '-',
    plaintiffName: transaction.plaintiffName ?? '-',
    memo: transaction.memo,
    destinationAccountName: transaction.destinationAccount?.name ?? '-',
    type: transaction.type,
    amount: numberToUSDCurrency(transaction.amount),
    runningBalance:
      transaction.runningBalance === '-'
        ? '-'
        : numberToUSDCurrency(transaction.runningBalance),
  }));

  return (
    <>
      {formattedList.map((tableItem, rowIndex) => {
        const color = getTypeColor(tableItem.type);
        const isScheduled = tableData[rowIndex]?.status === 'SCHEDULED';
        const isProcessing = tableData[rowIndex]?.status === 'PROCESSING';

        const borderClass =
          tableItem.type === 'CREDIT'
            ? classes.creditLeftBorder
            : tableItem.type === 'DEBIT'
              ? classes.debitLeftBorder
              : '';

        return (
          <TableRow
            key={`${tableItem.transactionId}/${tableItem.type}/${tableItem.lastUpdatedOn}`}
          >
            {Object.entries(tableItem).map(([key, item], idx) => (
              <TableCell
                key={`${tableItem.transactionId}_${idx}`}
                className={idx === 0 ? borderClass : undefined}
                style={
                  (key === 'type' || key === 'amount') && color
                    ? {
                        color,
                      }
                    : undefined
                }
              >
                {key === 'lastUpdatedOn' ? (
                  <Box className={classes.createdCellContent}>
                    <span>{item}</span>
                    {isScheduled && <span className={classes.queuedTag}>Queued</span>}
                    {isProcessing && <span className={classes.queuedTag}>Processing</span>}
                  </Box>
                ) : (key === 'memo' || key === 'caseReferenceId') ? (
                  <Tooltip
                    title={String(item)}
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: (theme) => ({
                          backgroundColor: theme.palette.common.white,
                          color: theme.palette.primary.light,
                          borderRadius: '5px',
                          boxShadow: '3px',
                          fontFamily: 'Inter',
                          fontSize: '13px',
                          fontWeight: 500,
                          padding: '5px',
                          maxWidth: '250px',
                        }),
                      },
                    }}
                  >
                    <span>{item}</span>
                  </Tooltip>
                ) : (
                  item
                )}
              </TableCell>
            ))}
          </TableRow>
        );
      })}
    </>
  );
};

export default FirmTransactionsTableDataRow;
